import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    input,
    linkedSignal,
    OnInit,
    Output,
    signal,
    ViewChild,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { Document, DocumentService } from "../../../services/document.service";
import { ReportService } from "../../../services/report.service";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { TableBuilder } from "../../bpe/s25.table.builder.component";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Doc } from "./s25.document.const";
import { Report, Report as Reports } from "../../../pojo/Report";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { TabGroupTab } from "../../../standalone/s25.tab.group.component";
import { VariableI } from "../../../pojo/VariableI";
import { BpeVarsUtil } from "../../bpe/s25.bpe.vars.util";
import { TelemetryService } from "../../../services/telemetry.service";
import { Bind } from "../../../decorators/bind.decorator";
import { DocumentReportBuilderComponent } from "./s25.document.report.builder.component";
import { DocumentUtil } from "./s25.document.util";
import { ReportBuilderUtil } from "./s25.report.builder.util";
import { BpeService } from "../../bpe/bpe.service";
import { BpeUtil } from "../../bpe/bpe.util";
import { UserStore } from "../../../stores/user.store";
import { s25Resource } from "../../../util/s25.resource";
import { GenericDropdownUtil } from "../../s25-dropdown/generic/generic.dropdown.util";
import Use = Report.Use;
import ObjectType = Report.ObjectType;

@TypeManagerDecorator("s25-ng-edit-document")
@Component({
    selector: "s25-ng-edit-document",
    template: `
        @if (isInit) {
            <div class="section">
                <label for="document-name" class="ngBold">Document Name</label>
                @if (readonly()) {
                    <span>{{ document.document_name }}</span>
                } @else {
                    <input
                        [(ngModel)]="document.document_name"
                        id="document-name"
                        name="document-name"
                        class="c-input"
                        type="text"
                        maxlength="64"
                    />
                }
                <label for="document-type" class="ngBold">Document Scope </label>
                @if (mode() === "edit" || readonly()) {
                    <div>
                        <p class="static-type">{{ type().label }}</p>
                    </div>
                } @else {
                    <s25-generic-dropdown
                        class="type-dropdown"
                        [items]="typeOptions()"
                        [placeholder]="'Document Scope'"
                        [value]="type()"
                        (valueChange)="onTypeChange($any($event))"
                    />
                }
                @if (scope() !== "subreport") {
                    <label for="output-filename" class="ngBold">Output Filename </label>
                    @if (readonly()) {
                        <span>{{ document.file_name ?? "" }}</span>
                    } @else {
                        <input
                            id="output-filename"
                            name="output-filename"
                            maxlength="200"
                            class="c-input"
                            [placeholder]=""
                            [(ngModel)]="document.file_name"
                            (ngModelChange)="onFileNameChange($event)"
                        />

                        @if (hasVarsOption() && checkBoxCompinit()) {
                            <div class="c-margin-bottom--single">{{ document.file_name }}</div>
                            <div class="small">Append variables to the output filename (optional).</div>
                            <div class="c-margin-bottom--single">
                                @for (option of objBasedVarsOptions(); track $index) {
                                    <div class="c-margin-left--double small">
                                        <s25-ng-checkbox
                                            [value]="option.isSelected"
                                            (valueChange)="onCheckVarsOptionBoxChange(option, $event)"
                                        ></s25-ng-checkbox>
                                        {{ option.itemName }}
                                    </div>
                                }
                            </div>
                        }
                    }
                }
                <label for="output-filename" class="ngBold description">Description </label>
                @if (readonly()) {
                    <div [innerHTML]="document.description ?? '' | safeHTML"></div>
                } @else {
                    <s25-ng-rich-text-editor [(modelValue)]="document.description" />
                }
            </div>

            @if (scope() !== "subreport" && (type().interface === "reportBuilder" || type().interface === "standard")) {
                <div class="section">
                    <label class="ngBold big">Options</label>
                    <div class="frame">
                        <label for="dm-page_orientation">Orientation</label>
                        @if (readonly()) {
                            <div>{{ Doc.pageOrientation[document.page_orientation] ?? Doc.DEFAULT_ORIENTATION }}</div>
                        } @else {
                            <select
                                id="dm-page_orientation"
                                class="cn-form__control"
                                [ngModel]="document.page_orientation ?? Doc.DEFAULT_ORIENTATION"
                                (ngModelChange)="document.page_orientation = $event"
                                [disabled]="readonly()"
                            >
                                @for (orientation of Doc.pageOrientationOptions; track $index) {
                                    <option [ngValue]="orientation.val">{{ orientation.txt }}</option>
                                }
                            </select>
                        }

                        <label>Margins</label>
                        <div class="margins">
                            @for (direction of ["Top", "Right", "Bottom", "Left"]; track $index) {
                                <div class="margin">
                                    <label for="dm-margin-{{ direction }}">{{ direction }}:</label>
                                    @if (readonly()) {
                                        <span>{{ margins[$index] ?? Doc.DEFAULT_MARGINS }} in</span>
                                    } @else {
                                        <div class="inputWithUnit">
                                            <input
                                                id="dm-margin-{{ direction }}"
                                                type="number"
                                                class="c-input"
                                                [(ngModel)]="margins[$index]"
                                                (ngModelChange)="onMarginsChange()"
                                                placeholder="inches"
                                                step="0.01"
                                                min="0"
                                            />
                                            <span class="unit" title="inches">in</span>
                                        </div>
                                    }
                                </div>
                            }
                        </div>

                        <label for="dm-font_size">Font Size</label>
                        @if (readonly()) {
                            <div>{{ document.font_size ?? Doc.DEFAULT_FONT_SIZE }} in</div>
                        } @else {
                            <div class="inputWithUnit">
                                <input
                                    id="dm-font_size"
                                    type="number"
                                    class="c-input"
                                    [ngModel]="document.font_size ?? Doc.DEFAULT_FONT_SIZE"
                                    (ngModelChange)="document.font_size = $event"
                                    step="0.1"
                                    min="0"
                                />
                                <span class="unit" title="em">em</span>
                            </div>
                        }

                        @if (document.document_type === "event" || document.document_type === "organization") {
                            <label id="dm-include_related">Include Related Events In Subreports</label>
                            <s25-toggle-button
                                [toggleLabel]="{ by: 'dm-include_related' }"
                                [value]="document.document_scope === 'related'"
                                (valueChange)="document.document_scope = $event ? 'related' : undefined"
                            />
                        }

                        @if (type().interface === "reportBuilder") {
                            <label for="dm-page_header_height">Header Height</label>
                            @if (readonly()) {
                                <div>{{ document.page_header_height ?? 0 }} em</div>
                            } @else {
                                <div class="inputWithUnit">
                                    <input
                                        id="dm-page_header_height"
                                        type="number"
                                        class="c-input"
                                        [(ngModel)]="document.page_header_height"
                                        step="0.1"
                                        min="0"
                                    />
                                    <span class="unit" title="inches">em</span>
                                </div>
                            }

                            <label for="dm-page_footer_height">Footer Height</label>
                            @if (readonly()) {
                                <div>{{ document.page_footer_height ?? 0 }} em</div>
                            } @else {
                                <div class="inputWithUnit">
                                    <input
                                        id="dm-page_footer_height"
                                        type="number"
                                        class="c-input"
                                        [(ngModel)]="document.page_footer_height"
                                        step="0.1"
                                        min="0"
                                    />
                                    <span class="unit" title="inches">em</span>
                                </div>
                            }

                            <label for="dm-page_footer_height">Apply Date Range Options</label>
                            @if (readonly()) {
                                <ul>
                                    @for (item of applyDateRange(); track item.itemId) {
                                        <li>{{ item.itemName }}</li>
                                    }
                                </ul>
                            } @else if (applyDateRangeOptions().length) {
                                <s25-ng-generic-multiselect-dropdown
                                    [(value)]="applyDateRange"
                                    [items]="applyDateRangeOptions()"
                                    (done)="onApplyDateRangeChange($event)"
                                />
                            }
                        }
                    </div>
                </div>
            }

            @if (type().interface === "standard") {
                @if (readonly()) {
                    <label class="ngBold">Header</label>
                    <div class="rendered" [innerHTML]="document.header_text ?? '' | safeHTML"></div>

                    <label class="ngBold">Body</label>
                    <div class="rendered" [innerHTML]="document.body_text ?? '' | safeHTML"></div>

                    <label class="ngBold">Footer</label>
                    <div class="rendered" [innerHTML]="document.footer_text ?? '' | safeHTML"></div>
                }
                @if (!readonly()) {
                    <label class="ngBold">Header</label>
                    <s25-ng-rich-text-editor
                        [(modelValue)]="document.header_text"
                        [defaultHeight]="300"
                    ></s25-ng-rich-text-editor>

                    <label class="ngBold">Body</label>
                    <s25-ng-rich-text-editor
                        [(modelValue)]="document.body_text"
                        [defaultHeight]="550"
                    ></s25-ng-rich-text-editor>

                    <label class="ngBold">Footer</label>
                    <s25-ng-rich-text-editor
                        [(modelValue)]="document.footer_text"
                        [defaultHeight]="300"
                    ></s25-ng-rich-text-editor>

                    <label class="ngBold">Page Footer</label>
                    <div class="page-footer">
                        <s25-ng-checkbox [(value)]="hidePageFooter">Hide Page Footer</s25-ng-checkbox>
                        <s25-ng-info-message [isPopover]="true">
                            This hides footer containing the print date and page number
                        </s25-ng-info-message>
                    </div>

                    <s25-simple-collapse
                        class="c-margin-top--single"
                        [expanded]="false"
                        [titleText]="'Template Variables'"
                        [header]="'Template Variables'"
                    >
                        <bpe-vars [documentType]="document.document_type" [type]="'document'"></bpe-vars>
                    </s25-simple-collapse>
                }
                <h3 class="ngBold c-margin-top--single">Preview</h3>
                <s25-ng-report-parameters [parameters]="parameters()" (valuesChange)="previewParameters = $event" />
                <s25-ng-button [type]="'outline'" [onClick]="showPreview">Preview</s25-ng-button>
            }

            @if (type().interface === "reportBuilder") {
                @if (scope() === "subreport") {
                    <label class="ngBold">Subreport</label>
                    <s25-ng-document-report-builder
                        [documentId]="id"
                        [type]="type().id"
                        [scope]="scope()"
                        [editable]="!readonly()"
                        (tableStrChange)="document.body_text = $event"
                    />

                    <ng-container [ngTemplateOutlet]="sortOrderTemplate" />
                } @else {
                    <div class="section">
                        <label class="ngBold big">Page Header</label>
                        @if (readonly()) {
                            <div class="rendered" [innerHTML]="document.header_text ?? '' | safeHTML"></div>
                        } @else {
                            <s25-ng-rich-text-editor
                                [(modelValue)]="document.header_text"
                                [defaultHeight]="300"
                                (modelValueChange)="validateReportBuilderHeader()"
                            />
                            @for (error of reportBuilderHeaderErrors(); track $index) {
                                <p class="field-error">{{ error }}</p>
                            }
                        }
                    </div>

                    <div class="section page-body">
                        <label class="ngBold big ">Page Body</label>
                        <div class="frame">
                            <s25-ng-document-report-builder
                                [documentId]="id"
                                [type]="type().id"
                                [scope]="scope()"
                                [editable]="!readonly()"
                                (tableStrChange)="document.body_text = $event"
                            />

                            <div class="sort-by">
                                <ng-container [ngTemplateOutlet]="sortOrderTemplate" />
                            </div>
                        </div>
                    </div>

                    <div class="section">
                        <label class="ngBold big">Page Footer</label>
                        @if (readonly()) {
                            <div class="rendered" [innerHTML]="document.footer_text ?? '' | safeHTML"></div>
                        } @else {
                            <s25-ng-rich-text-editor
                                [(modelValue)]="document.footer_text"
                                [defaultHeight]="300"
                                (modelValueChange)="validateReportBuilderFooter()"
                            />
                            @for (error of reportBuilderFooterErrors(); track $index) {
                                <p class="field-error">{{ error }}</p>
                            }
                        }
                    </div>

                    @if (!readonly()) {
                        <s25-simple-collapse [expanded]="false" [header]="'Template Variables'">
                            <div style="padding: 1em;">
                                <p>
                                    Select details you would like to include and copy the generated variable values into
                                    your <em class="bold">header</em> or <em class="bold">footer</em>. Clicking a
                                    variable link will copy that value to your clipboard.
                                </p>

                                <label class="dropdownLabel">Report Variables</label>
                                <s25-ng-bpe-variable
                                    [state]="{ options: {} }"
                                    [type]="'reservationsHeaderFooter'"
                                    [documentType]="document.document_type"
                                />
                            </div>
                        </s25-simple-collapse>
                    }

                    <h3 class="ngBold c-margin-top--single">Preview</h3>
                    <s25-ng-report-parameters [parameters]="parameters()" (valuesChange)="previewParameters = $event" />
                    <s25-ng-button [type]="'outline'" [onClick]="showReportBuilderPreview">Preview</s25-ng-button>
                }
            }
            @if (type().interface === "tableBuilder") {
                @if (hasApplyDateRange()) {
                    <div class="section">
                        <label class="ngBold big">Report Options</label>
                        <div class="frame">
                            <label for="dm-page_footer_height">Apply Date Range Options</label>
                            @if (readonly()) {
                                <ul>
                                    @for (item of applyDateRange(); track item.itemId) {
                                        <li>{{ item.itemName }}</li>
                                    }
                                </ul>
                            } @else if (applyDateRangeOptions().length) {
                                <s25-ng-generic-multiselect-dropdown
                                    [(value)]="applyDateRange"
                                    [items]="applyDateRangeOptions()"
                                    (done)="onApplyDateRangeChange($event)"
                                />
                            }
                        </div>
                    </div>
                }

                @if (view === "form") {
                    @if (type().tableBuilder.length > 1 && view === "form") {
                        <s25-ng-tab-group [tabs]="type().tableBuilder" [(chosen)]="tableTab" />
                    }
                    @for (table of type().tableBuilder; track $index) {
                        @if (table.id === tableTab().id) {
                            @if (type().tableBuilder.length === 1) {
                                <h3 class="ngBold c-margin-top--single">{{ table.label }}</h3>
                            }
                            <s25-ng-table-builder
                                [(columns)]="tables[$index]"
                                [variableType]="table.key"
                                [dateFormatType]="'wsDateFormats'"
                                [hasDataColumn]="true"
                                [hasWidthColumn]="true"
                                [maxWidth]="10"
                                [editable]="!readonly()"
                                (columnsChange)="onColumnsChange()"
                            />
                        }
                    }
                } @else if (view === "code") {
                    <h3 class="ngBold c-margin-top--single">Data</h3>
                    @if (readonly()) {
                        <div class="rendered">{{ document.body_text ?? "" }}</div>
                    } @else {
                        <textarea [(ngModel)]="document.body_text" class="codeMode"></textarea>
                    }
                }

                <div class="sort-by">
                    <ng-container [ngTemplateOutlet]="sortOrderTemplate" />
                </div>

                @if (!readonly() && userStore.isService25()) {
                    <h3 class="ngBold c-margin-top--single">Advanced</h3>
                    <button class="aw-button aw-button--outline d-block" (click)="toggleView()">
                        {{ view === "form" ? "Code" : "Form" }} View
                    </button>
                }

                <h3 class="ngBold c-margin-top--single">Preview</h3>
                <s25-ng-report-parameters [parameters]="parameters()" (valuesChange)="previewParameters = $event" />
                <s25-ng-button [type]="'outline'" [onClick]="showQueryPreview">Preview</s25-ng-button>
                <s25-ng-modal #confirmExitCodeMode [type]="'confirm'" [title]="'Switch to Form View'">
                    Changes made to code may be lost when switching to Form View. Are you sure that you want to switch?
                </s25-ng-modal>
            }
            @if (isTemplate && mode() !== "copy" && !!document.sampleFilename) {
                <label class="ngBold" for="preview-reference">Sample</label>
                <s25-ng-button [type]="'outline'" (clicked)="onSampleClick()">View Sample</s25-ng-button>
            }

            <div class="buttons c-margin-top--single">
                @if (mode() !== "view") {
                    <s25-ng-button [type]="'primary'" [onClick]="onSave">Save</s25-ng-button>
                } @else {
                    <s25-ng-button [type]="'primary'" [onClick]="onCopy">Copy</s25-ng-button>
                }
                <s25-ng-button [type]="'outline'" [onClick]="onCancel">
                    {{ mode() !== "view" ? "Cancel" : "Close" }}
                </s25-ng-button>
            </div>
        }

        <ng-template #sortOrderTemplate>
            <label for="dm-sort_order">Sort By</label>
            <div class="sortOrders">
                @for (sort of sortOrders; track sort) {
                    <div class="sortBy">
                        @if (readonly() && ($first || sortOrders[$index - 1].val !== "none")) {
                            @if (!$first) {
                                <span> then </span>
                            }
                            <div>{{ sort.txt ?? "" }}</div>
                        } @else if (!readonly()) {
                            @if (!$first) {
                                <span> then </span>
                            }
                            <select
                                class="cn-form__control"
                                [(ngModel)]="sort.val"
                                (ngModelChange)="onSortOrderChange()"
                            >
                                <option [ngValue]="'none'" selected>None</option>
                                @for (option of sortOrderOptions(); track $index) {
                                    @if (option.val) {
                                        <option [ngValue]="option.val">{{ option.txt }}</option>
                                    }
                                }
                            </select>
                        }
                    </div>
                }
            </div>
        </ng-template>
    `,
    styles: `
        /* Date picker and dropdown might leave the screen add some extra space */
        :host {
            display: block;
            padding-bottom: 10rem;
        }

        label {
            display: block;
        }

        label:not(:first-child) {
            margin-top: 1em;
        }

        .buttons {
            display: flex;
            gap: 0.5em;
        }

        .codeMode {
            width: 100% !important;
            min-height: 5.5em;
            padding: 0.5em;
        }

        ::ng-deep s25-ng-edit-document s25-ng-rich-text-editor .tox-tinymce {
            width: 100% !important;
        }

        #preview-reference {
            margin-right: 0.5em;
        }

        ::ng-deep .s25-multiselect-popup-container {
            --multiselect--preferred-width: 50vw;
        }

        ::ng-deep .s25-multiselect-popup .s25-multiselect-columns-container {
            max-height: 35vh;
        }

        #output-filename {
            width: min(100%, 20em);
        }

        .static-type {
            padding-top: 0.25rem;
        }

        .rendered {
            background-color: #f6f6f6;
            padding: 0.5rem;
            border: 1px dashed;
            margin-top: 0.25rem;
        }

        s25-ng-tab-group {
            padding-top: 1rem;
        }

        ::ng-deep .nm-party--on s25-ng-edit-document .rendered {
            background-color: #4c4d55;
        }

        .dropdownLabel {
            display: inline-block !important;
            font-weight: bold;
            margin-top: 1em;
            margin-bottom: 0;
            width: min(100%, 225px);
        }

        ::ng-deep s25-ng-edit-document s25-ng-bpe-variable .ngDropdownPaginated {
            max-width: 250px;
        }

        .margins {
            display: flex;
            gap: 1rem;
        }

        .margin {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }

        .margin label {
            font-style: italic;
        }

        .margin input {
            max-width: 5em;
        }

        .sortOrders {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
        }

        .sortBy {
            display: flex;
            gap: 0.5rem;
            align-items: center;
        }

        .inputWithUnit {
            display: flex;
            gap: 0.25rem;
            align-items: center;
        }

        .inputWithUnit > input {
            max-width: 5em;
        }

        .type-dropdown {
            max-width: 20em;
            display: block;
        }

        ::ng-deep s25-ng-edit-document .type-dropdown .ngDropdownScrollTopEl {
            max-height: 50dvb;
        }

        .section {
            padding-bottom: 4rem;
        }

        .big {
            font-size: 1.1em;
        }

        .frame {
            border: 1px solid #ccc;
            padding: 1rem;
        }

        .page-body .frame {
            padding: 0;
            padding-inline: 1rem;
        }

        .options-wrapper {
            padding: 1rem;
        }

        ::ng-deep .nm-party--on s25-ng-edit-document .frame {
            border-color: #666;
        }

        s25-ng-document-report-builder {
            display: block;
        }
        em.bold {
            font-weight: bold;
            font-style: normal;
        }

        s25-simple-collapse {
            display: block;

            bpe-vars {
                display: block;
                padding: 1em;
            }
        }

        .page-footer {
            display: flex;
            gap: 0.5rem;
            align-items: center;
        }

        .field-error {
            color: var(--color--danger);
            font-size: 0.85rem;
        }

        .sort-by {
            padding-block: 0.5rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditDocumentComponent implements OnInit {
    @Input() id: number;
    readonly mode = input<Doc.Mode>("edit");
    @Input() isTemplate: boolean = false;
    readonly scopeInput = input<Doc.Scope>(undefined, { alias: "scope" });

    // Emits document ID
    @Output() saved = new EventEmitter<number>();
    @Output() copied = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    @ViewChild("confirmExitCodeMode") confirmExitCodeMode: S25ModalComponent;
    private readonly reportBuilder = viewChild(DocumentReportBuilderComponent);

    readonly userStore = inject(UserStore);

    private readonly defaultFilenames = s25Resource(ReportService.getDefaultDocumentFileNames);

    protected readonly scope = linkedSignal(() => this.scopeInput());
    protected readonly readonly = computed(() => this.mode() === "view");
    protected readonly typeOptions = computed(() => {
        const types = this.scope() === "subreport" ? Doc.subreportTypes : Doc.defaultTypes;
        const options = DocumentUtil.typesToDropdown(types);

        if (this.mode() === "create" && this.scope() !== "subreport") {
            const defaultFilenames = this.defaultFilenames.value();
            if (!defaultFilenames) return []; // Wait for filenames to load
            return options.filter((item) => defaultFilenames.hasOwnProperty(item.reportId));
        } else {
            return options;
        }
    });
    protected readonly sortOrderOptions = computed(() => {
        if (this.type().interface === "reportBuilder") {
            const key = ReportBuilderUtil.mapBpeVariablesKey(<Doc.ReportBuilderTypeId>this.type().id);
            return BpeVarsConst.Items[key].filter((item) => !!item.val && item.sortable !== false);
        }
        if (this.type().id === Doc.type.rsrvListing.id) {
            // Occurrences By Date should not have any "op_task_*" options
            return BpeVarsConst.Items["reservations"].filter(
                (item) => !!item.val && item.sortable !== false && !item.val.startsWith("op_task"),
            );
        }
    });

    isInit = false;
    readonly type = signal<Doc.Type & DropDownItem>(undefined);
    document: Document;
    originalDocument: Document;
    tables: TableBuilder.Column[][] = [];
    readonly tableTab = signal<TabGroupTab>(undefined);
    view: "code" | "form" = "form";
    margins: number[] = Doc.DEFAULT_MARGINS.match(/[\d.]+/g).map(Number);
    sortOrders: { val: string; txt: string }[] = [
        { val: "none", txt: "None" },
        { val: "none", txt: "None" },
        { val: "none", txt: "None" },
    ];
    protected readonly applyDateRangeOptions = computed(() => {
        const params = this.shellReport.lastValue()?.parameter ?? [];
        const validate = params.find((param) => /Apply Date Range/.test(param.parameter_name))?.validate;
        const options = GenericDropdownUtil.extractItems(validate?.split(",") ?? []) ?? [];
        for (const opt of options) opt.itemName = opt.itemName?.replace("Reservation", "Occurrence");
        return options;
    });

    readonly objBasedVarsOptions = signal<DropDownItem[]>([
        { itemId: "eventName", itemName: "Event Name", val: "$pro.vars.eventName", isSelected: false },
        { itemId: "eventId", itemName: "Event Id", val: "$pro.vars.eventId", isSelected: false },
        { itemId: "eventTitle", itemName: "Event Title", val: "$pro.vars.eventTitle", isSelected: false },
        {
            itemId: "Event Start",
            itemName: "Event Start",
            val: "$pro.vars.dates.eventStartDateString",
            isSelected: false,
        },
        { itemId: "eventLocator", itemName: "Event Reference", val: "$pro.vars.eventLocator", isSelected: false },
    ]);

    readonly checkBoxCompinit = signal(false);
    readonly hidePageFooter = signal(false);
    private readonly documentReportId = signal<number | undefined>(undefined);

    private readonly shellReport = s25Resource({
        lazy: true,
        params: () => this.type(),
        loader: async ({ params: documentType, abortSignal }) => {
            if (!documentType?.reportId) return;
            const shell = await ReportService.getReportTemplate(documentType.reportId);
            if (!shell || "message" in shell) return;
            return shell;
        },
    });
    private readonly documentReport = s25Resource({
        params: () => this.documentReportId(),
        loader: async ({ params: reportId, abortSignal }) => {
            return ReportService.getReportMeta(reportId);
        },
    });
    readonly parameters = computed(() => {
        const params = this.documentReport.lastValue()?.parameter ?? this.shellReport.lastValue()?.parameter ?? [];

        // Set "Apply Date Range" options to all available options
        const applyDateRangeOptions = this.applyDateRangeOptions().map((item) => item.itemId);
        return params.map((param) => {
            if (/Apply Date Range/i.test(param.parameter_name)) {
                return { ...param, validate: applyDateRangeOptions.join(",") };
            }
            return { ...param };
        });
    });
    previewParameters: Record<Report.Parameter["parameter_field_mapping"], Report.ParameterValue> = {};
    protected hasApplyDateRange = computed(() => {
        const type = this.type();
        if (!type) return false;
        if (type.interface === "tableBuilder") return type.id === "rsrvListing";
        if (type.interface === "reportBuilder") return this.scope() !== "subreport";
        return false;
    });
    protected readonly applyDateRange = linkedSignal<DropDownItem[]>(() => {
        if (!this.hasApplyDateRange()) return [];
        const parameters = this.documentReport.lastValue()?.parameter ?? this.shellReport.lastValue()?.parameter ?? [];
        const parameter = parameters.find((param) => /Apply Date Range/.test(param.parameter_name));
        if (!parameter) return [];
        const selected = parameter.validate?.split(",").filter((item) => !!item) ?? [];
        const options = this.applyDateRangeOptions().map((item) => item.itemId);
        return GenericDropdownUtil.extractItems(selected.filter((item) => options.includes(item))) ?? [];
    });

    protected readonly Doc = Doc;
    protected readonly BpeVarsConst = BpeVarsConst;

    constructor(private changeDetector: ChangeDetectorRef) {
        effect(() => this.onReportBuilderChange());
        effect(() => {
            if (!this.hasApplyDateRange()) return;
            this.document.apply_date_range = this.applyDateRange()
                ?.map((item) => item.itemId)
                .join(",");
        });
    }

    async ngOnInit() {
        if (this.mode() === "create") {
            this.document = this.newDocument();
            const defaultFilenames = await this.defaultFilenames.awaitLoad(); // Only need to await it if we are creating
            this.document.file_name = defaultFilenames![Doc.type[this.document.document_type].reportId];
        } else {
            this.document = await DocumentService.getDocument(this.id);
        }
        this.scope.set(this.document.document_scope);

        if (this.mode() === "copy") {
            delete this.document.document_id; // No id => new document will be created
            this.document.document_name += " copy";
        }
        this.documentReportId.set(this.document.report_id);
        this.hidePageFooter.set(!!this.document.footer_text?.includes("<!--{{ws.HidePageFooter}}-->"));
        const type = Doc.type[this.document.document_type];
        this.type.set({ ...type, itemId: type.id });
        this.originalDocument = S25Util.deepCopy(this.document);
        this.parseDocument();

        if (this.document.document_type === "reservations") {
            this.validateReportBuilderHeader();
            this.validateReportBuilderFooter();
        }

        if (this.hasVarsOption()) {
            this.isVarSelected();
            this.checkBoxCompinit.set(true);
        }

        this.isInit = true;
        this.changeDetector.detectChanges();
    }

    newDocument(): Document {
        const document: Document = {
            document_name: "New Document",
            document_type: "event",
        } as Document;
        if (this.scope() === "subreport") {
            document.document_type = "rsrvReq";
            document.document_scope = "subreport";
            document.sort_order = this.getDefaultSortOrder(document.document_type);
        }
        return document;
    }

    getText() {
        let { header_text, body_text, footer_text } = this.document;

        // Apply "Hide Page Footer"
        footer_text = footer_text?.replace(/<!--{{ws.HidePageFooter}}-->/g, "") ?? "";
        if (this.hidePageFooter()) {
            footer_text += "<!--{{ws.HidePageFooter}}-->";
        }

        return { header_text, body_text, footer_text };
    }

    @Bind
    async showPreview() {
        if (!this.validatePreviewParameters()) return;

        //todo: related events
        //todo: specific organization (billing)
        //todo: calculate total price from eventData (potentially with related events and potentially only for specific org id)
        //todo: override primary org with org id sent it (if any)
        const { header_text, body_text, footer_text } = this.getText();

        const { id: runId } = await DocumentService.getEventDocumentRunIdFromText(
            {
                document_id: undefined,
                document_scope: this.document.document_scope,
                header_text,
                body_text,
                footer_text,
                margins: this.document.margins ?? Doc.DEFAULT_MARGINS,
                font_size: this.document.font_size ?? Doc.DEFAULT_FONT_SIZE,
                page_orientation: this.document.page_orientation ?? Doc.DEFAULT_ORIENTATION,
            },
            <number>this.previewParameters.num_parm1,
        );

        const context = { ...this.previewParameters, documentRunId: String(runId) };
        if (this.document.document_type === "event") {
            const id = this.document.report_id || Reports.Reports.EventShell.id;
            return this.putReportRequest(id, "Event Contract", context);
        }
        if (this.document.document_type === "reservation") {
            const id = this.document.report_id || Reports.Reports.ReservationShell.id;
            return this.putReportRequest(id, "Reservation", { ...context });
        }
        if (this.document.document_type === "organization") {
            const id = this.document.report_id || Reports.Reports.OrganizationShell.id;
            return this.putReportRequest(id, "Event" + " Organization", context);
        }
        if (this.document.document_type === "invoice") {
            const id = this.document.report_id || Reports.Reports.PaymentShell.id;
            return this.putReportRequest(id, "Invoice", { ...context });
        }
    }

    async putReportRequest(id: number, name: string, context?: { [key: string]: any }, devMode: boolean = false) {
        let customFileName = this.document.file_name;
        const filename = String(this.document.file_name ?? "");
        const hasVariables = this.objBasedVarsOptions().some((o) => filename.includes(String(o.val)));
        if (hasVariables) {
            const eventData = await BpeService.getEventData(this.previewParameters.num_parm1);
            const vars = await BpeUtil.getVariables(eventData, null, eventData);
            // Replace vars filename
            customFileName = BpeUtil.interpolateString(vars, filename);
        }

        const [response, error] = await S25Util.Maybe(
            ReportService.putReportRequest({
                report: {
                    rpt_id: id,
                    rpt_name: name,
                    rpt_engine: "WS",
                    rpt_use: Use.None,
                    object_type: ObjectType.NonSpecific,
                },
                context: context,
                format: null,
                delivery: null,
                otherRecipients: null,
                otherMessage: null,
                customFilename: customFileName,
                query: devMode ? "&dev_mode=T" : "",
            }),
        );
        if (error) return this.onError(error);

        if (typeof response === "string") alert(response);
    }

    @Bind
    async onSave() {
        await S25Util.delay(0); // Give dropdowns time to close and emit
        if (!this.validate()) return;

        if (DocumentUtil.canReferenceDocuments(this.document)) {
            this.document.subreports = ReportBuilderUtil.getSubreportIds(
                this.document.body_text,
                this.type().id as Doc.ReportBuilderTypeId,
            );
        }

        Object.assign(this.document, this.getText()); // Apply transformations

        const originalDocument = this.mode() === "edit" ? this.originalDocument : undefined;
        const documentId = await DocumentService.saveDocument(this.document, originalDocument);
        if (!documentId) return;

        const telemetrySub = this.isTemplate ? "DM_Documents" : "DM_Templates";
        const telemetryType = `DM_${S25Util.firstCharToUpper(this.mode())}`;
        TelemetryService.sendWithSub("DocMgmt", telemetrySub, telemetryType);

        this.saved.emit(documentId);
    }

    validate() {
        const doc = this.document;
        let message: string;
        if (!doc.document_name) message = "Please enter a name";
        else if (!doc.body_text) message = "Please enter a body";
        else if (this.type().interface === "reportBuilder" && !this.reportBuilder()?.valid()) {
            message = "Please fix the table errors before saving";
        } else if (this.hasApplyDateRange() && !this.applyDateRange()?.length) {
            message = "Please select at least one option for Apply Date Range";
        } else if (this.type().interface === "tableBuilder" && this.view === "form") {
            // Check that columns requiring an ID have one
            outer: for (const table of this.tables) {
                for (const column of table) {
                    const requireData = column.variable?.dataType && column.variable.dataType !== "format";
                    if (!column.variable) message = `Please select a variable for column ${column.header}`;
                    if (requireData && !column.data) message = `Please select an item for column ${column.header}`;
                    if (message) break outer;
                }
            }
        }

        if (this.hasVarsOption()) {
            const fileName = doc.file_name?.trim() || "";
            if (!fileName) {
                message = "Please enter an output filename";
            } else if (fileName.startsWith("{{$pro.vars.")) {
                message = "Output filename cannot start with {{$pro.vars.";
            }
        }

        if (message) {
            alert(message);
            return false;
        }
        return true;
    }

    @Bind
    onCopy() {
        this.copied.emit();
    }

    @Bind
    onCancel() {
        this.cancelled.emit();
    }

    async onError(error: any) {
        const text = (await error?.error?.text?.()) as string;
        if (text?.includes("An error occurred during transform processing")) {
            const msg = `Something went wrong while processing the document.<br>Please make sure that the document is valid and try again.`;
            alert(msg);
            return;
        }
    }

    onTypeChange(type: Doc.Type) {
        // reset header, text and footer when type switch from an excel type
        if (this.type().interface === "tableBuilder") {
            this.document.header_text = "";
            this.document.body_text = "";
            this.document.footer_text = "";
        }
        // If file name is still default, update it to the new default file name
        if (
            this.document.file_name === this.defaultFilenames.value()![this.type().reportId] ||
            this.mode() === "create"
        ) {
            this.document.file_name = this.defaultFilenames.value()![type.reportId];
        }
        // Update variables
        this.type.set(type);
        this.document.document_type = type.id;

        // If new type is table builder, set default columns
        if (type.interface === "tableBuilder") {
            this.view = "form";
            this.tables = type.tableBuilder.map((table) => {
                const variables = BpeVarsConst.Items[table.key].filter((item) => !item.isGroup);
                return variables.map((item) => ({
                    header: item.txt,
                    variable: item,
                    width: item.width,
                }));
            });
            this.tableTab.set(type.tableBuilder[0]);
            this.updateQueryVariable();
            this.document.file_name = this.document.file_name.replace(/.pdf$/, ".xml"); // Switch extension from pdf to xml
            if (type.id === "rsrvListing") {
                this.document.sort_order = this.getDefaultSortOrder(type.id);
                this.parseSortOrder();
            }
        }

        // If new type is report builder, set default layout properties
        if (type.interface === "reportBuilder") {
            this.document.group_by = "none";
            this.document.page_break = "none";
            this.document.margins = "0.25in 0.5in 0.17in 0.5in";
            this.document.font_size = 0.9;
            this.document.page_orientation = "portrait";
            this.document.page_header_height = 3;
            this.document.page_footer_height = 2.5;
            this.document.sort_order = this.getDefaultSortOrder(this.type().id);
            this.parseDocument();
        }
    }

    async toggleView() {
        if (this.view === "code") {
            const ok = await this.confirmExitCodeMode.open();
            if (!ok) return;
            this.view = "form";
            this.parseDocument();
            this.changeDetector.detectChanges();
        } else {
            this.view = "code";
        }
    }

    onColumnsChange() {
        this.updateQueryVariable();
    }

    updateQueryVariable() {
        const type = this.type();
        if (type.interface !== "tableBuilder") return;

        this.document.body_text = this.tables
            .map((table, i) => {
                const columns = BpeVarsUtil.getTableBuilderColumnsStr(table, true);
                if (!columns) return "";
                return `{{for: ${type.tableBuilder[i].variable}: ${columns} :end-for}}`;
            })
            .join("\n\n");
    }

    @Bind
    async showQueryPreview() {
        if (!this.validatePreviewParameters()) return;

        const [documentRun, err] = await S25Util.Maybe(
            DocumentService.setDocumentRun({
                document_id: this.document.document_id,
                header_text: "",
                body_text: this.document.body_text,
                footer_text: "",
                document_scope: this.document.document_scope,
            }),
        );
        if (err) return this.onError(err);

        const context = { ...this.previewParameters, documentRunId: String(documentRun.root.itemId) };

        return this.putReportRequest(this.document.report_id || this.type().reportId, this.type().label, context, true);
    }

    validatePreviewParameters() {
        for (const param of this.parameters()) {
            if (param.required === "F") continue;
            const value = this.previewParameters[param.parameter_field_mapping];
            if (S25Util.isDefined(value)) continue;
            const paramName = Doc.parameterNameMap[param.parameter_name] ?? param.parameter_name;
            alert(`Please enter a value for parameter ${paramName}`);
            return false;
        }
        return true;
    }

    findVariableAndData(variables: VariableI[], varStr: string) {
        for (const variable of variables) {
            if ("dataType" in variable) {
                // Need to match to variable and data, for example
                // deposit.paymentStatus => $data.paymentStatus, data = deposit
                // totalTaxType.123 => totalTaxType.$data, data = 123
                const reVar = S25Util.escapeRegExp(variable.val).replace(/\\\$data/g, "(.*)");
                const regex = new RegExp(`^${reVar}$`);
                const match = varStr.trim().match(regex);
                if (!match) continue;

                const data = match[1]; // First matched $data
                return { variable, data };
            } else if (varStr.trim() === variable.val) {
                return { variable };
            }
        }
        return {};
    }

    parseDocument() {
        this.parseMargins();
        this.parseSortOrder();
        this.parseApplyDateRange();
        // If the type is table builder then we need to parse the body text to get the columns
        const type = this.type();
        if (type.interface === "tableBuilder") return this.parseTableBuilder();
    }

    parseTableBuilder() {
        const type = this.type();
        if (type.interface !== "tableBuilder") return;

        this.tableTab.set(type.tableBuilder[0]);
        this.tables = new Array(type.tableBuilder.length).fill(null).map((_) => [] as TableBuilder.Column[]);
        for (const { groups } of Array.from(this.document.body_text.matchAll(Doc.tableBuilderRegex))) {
            const i = type.tableBuilder.findIndex((table) => table.variable === groups.variable);
            const table = type.tableBuilder[i];
            if (!table) continue;

            const variables = BpeVarsConst.Items[table.key];
            const columns: TableBuilder.Column[] = [];
            const tableMatches = Array.from(groups.table.matchAll(Doc.tableBuilderColumnRegex));
            for (const { groups } of tableMatches) {
                const { variable, data } = this.findVariableAndData(variables, groups.variable);
                if (!variable) continue;

                const formatName = groups.format?.trim() || "";
                const format = formatName && BpeVarsConst.Items.dateFormats.find((format) => format.val === formatName);
                columns.push({
                    header: groups.header.trim(),
                    variable,
                    width: Number(groups.width) || 1, // Default to 1 if no width is provided
                    format,
                    data: data && { val: data },
                });
            }

            this.tables[i] = columns;
        }
    }

    onSampleClick() {
        if (!this.document.sampleFilename) return;
        DocumentService.downloadSample(this.document.document_id, this.document.sampleFilename);
    }

    @Bind
    async showReportBuilderPreview() {
        if (!this.validatePreviewParameters()) return;

        const documentRun = await DocumentService.setDocumentRun({
            document_id: undefined,
            header_text: this.document.header_text,
            body_text: this.document.body_text,
            footer_text: this.document.footer_text,
            document_scope: this.document.document_scope,
            group_by: this.document.group_by,
            sort_order: this.document.sort_order,
            page_break: this.document.page_break,
            margins: this.document.margins,
            font_size: this.document.font_size,
            page_orientation: this.document.page_orientation,
            page_header_height: this.document.page_header_height,
            page_footer_height: this.document.page_footer_height,
        });
        const runId = documentRun.root.itemId;

        const context = { ...this.previewParameters, documentRunId: String(runId) };
        let reportId = this.document.report_id || Reports.Reports.ReservationsShell.id;
        // TODO: Remove with ANG-7521
        if (this.document.document_type === "reservations") reportId = Reports.Reports.ReservationsShell.id;
        return this.putReportRequest(reportId, this.type().label, context);
    }

    parseMargins() {
        if (!this.document.margins) return;
        this.margins = this.document.margins.match(/[\d.]+/g)?.map(Number) ?? this.margins;
    }

    onMarginsChange() {
        this.document.margins = `${this.margins[0]}in ${this.margins[1]}in ${this.margins[2]}in ${this.margins[3]}in`;
    }

    getDefaultSortOrder(type: string) {
        return Doc.reportBuilderDefaultSort[type as keyof typeof Doc.reportBuilderDefaultSort]?.join(",") ?? "";
    }

    parseSortOrder() {
        if (!this.document.sort_order) return;
        const sortOrder = (this.document.sort_order.match(/[\w_]+/g) as string[]) ?? [];
        while (sortOrder.length < 3) sortOrder.push("none");

        const variables = this.sortOrderOptions();
        this.sortOrders = sortOrder.map((val) => {
            if (val === "none") return { val, txt: "None" };
            const variable = variables?.find((variable) => "val" in variable && variable.val === val);
            if (variable) return { ...(variable as { val: string; txt: string }) };
            return { val, txt: "None" };
        });
    }

    onSortOrderChange() {
        this.sortOrders.sort((a, b) => {
            if (a.val === "none" && b.val === "none") return 0;
            if (a.val === "none") return 1;
            if (b.val === "none") return -1;
            return 0;
        });
        this.document.sort_order = this.sortOrders
            .filter((order) => order.val !== "none")
            .map((order) => order.val)
            .join(",");
    }

    parseApplyDateRange() {
        const name = Object.fromEntries(this.applyDateRangeOptions().map((item) => [item.itemId, item.itemName]));
        this.applyDateRange.set(
            this.document.apply_date_range
                ?.split(",")
                .filter((item) => !!item)
                .map((item) => ({ itemId: item, itemName: name[item] })),
        );
    }

    onApplyDateRangeChange(items: DropDownItem[]) {
        this.document.apply_date_range = items.map((item) => item.itemId).join(",");
    }

    hasVarsOption(): boolean {
        const t = this.type();
        return (
            t.category === "Object Based" && ["event", "reservation", "invoice"].some((k) => String(t.id).includes(k))
        );
    }

    onCheckVarsOptionBoxChange(option: DropDownItem, isChecked: boolean) {
        const placeholder = `{{${option.val}}}`;
        const originalName = this.document.file_name || "";

        // Extract extension (default .pdf)
        const extMatch = originalName.match(/(\.\w+)$/);
        const ext = extMatch ? extMatch[1] : ".pdf";
        let baseName = extMatch ? originalName.slice(0, -ext.length) : originalName;

        if (isChecked) {
            if (!this.validateFilenameHasBase()) {
                alert("Please enter a base filename before adding variables.");
                return;
            }

            // Add only if not already present
            if (!baseName.includes(placeholder)) {
                baseName += placeholder;
            }
        } else {
            // Remove only this placeholder
            const regex = new RegExp(S25Util.escapeRegExp(placeholder), "g");
            baseName = baseName.replace(regex, "");
        }

        this.document.file_name = baseName + ext;
    }

    isVarSelected() {
        if (!this.document?.file_name) return false;
        const updated = this.objBasedVarsOptions().map((option) => {
            const placeholder = `{{${option.val}}}`;
            return {
                ...option,
                isSelected: this.document.file_name.includes(placeholder),
            };
        });
        this.objBasedVarsOptions.set(updated);
    }

    validateFilenameHasBase(): boolean {
        const name = this.document.file_name || "";
        // Step 1 — remove extension
        const extIndex = name.lastIndexOf(".");
        const withoutExt = extIndex > -1 ? name.substring(0, extIndex) : name;
        // Step 2 — remove all variable placeholders {{xxx}}
        const withoutVars = withoutExt.replace(/\{\{.*?\}\}/g, "");
        // Step 3 — trim spaces
        const base = withoutVars.trim();
        // Step 4 — base must contain at least one valid character (letters, numbers, _, -)
        return /[A-Za-z0-9_-]/.test(base);
    }

    onFileNameChange(fileName: string) {
        this.document.file_name = fileName;
        if (!fileName || !fileName.trim()) {
            this.objBasedVarsOptions.set(
                this.objBasedVarsOptions().map((opt) => ({
                    ...opt,
                    isSelected: false,
                })),
            );
        } else if (this.hasVarsOption()) {
            this.isVarSelected();
        }
    }

    protected onReportBuilderChange() {
        const groups = this.reportBuilder()?.reportBuilder()?.groups();
        const groupBy = <typeof this.document.group_by>groups
            ?.filter((group) => group.groupBy())
            .map((group) => group.groupBy())
            .join(",");

        const pageBreak = <typeof this.document.page_break>groups
            ?.filter((group) => group.groupBy() && group.pageBreak())
            .map((group) => group.groupBy())
            .join(",");

        if (!this.document) return;
        this.document.group_by = groupBy;
        this.document.page_break = pageBreak;
    }

    protected reportBuilderHeaderErrors = signal<string[]>([]);
    protected validateReportBuilderHeader() {
        const text = this.document.header_text;
        const errors: string[] = [];
        if (text && ReportBuilderUtil.ReservationsMigration.groupByRegex.test(text)) {
            errors.push("The {{ws.groupBy}} variable is no longer supported and will be ignored");
        }
        this.reportBuilderHeaderErrors.set(errors);
    }

    protected reportBuilderFooterErrors = signal<string[]>([]);
    protected validateReportBuilderFooter() {
        const text = this.document.footer_text;
        const errors: string[] = [];
        if (text && ReportBuilderUtil.ReservationsMigration.groupByRegex.test(text)) {
            errors.push("The {{ws.groupBy}} variable is no longer supported and will be ignored");
        }
        this.reportBuilderFooterErrors.set(errors);
    }

    protected readonly Report = Report;
}
